import { useEffectOnce } from 'react-use';
import Button from '@components/Button';
import Form from '@components/Form';
import InputText from '@components/InputText';
import { type ResolvedIdp, resolveIdpFromEmail } from '@helpers/auth/domainIdpMap';
import { isPasswordSignInEnabled } from '@helpers/auth/passwordSignIn';
import { useForm } from '@hooks/form';
import * as yup from 'yup';

import { SIGN_IN_UNAVAILABLE_MESSAGE } from './errorMessages';

interface Props {
  initialEmail?: string;
  onResolved: (email: string, result: ResolvedIdp) => void;
}

const validationSchema = yup.object().shape({
  email: yup.string().email().required(),
});

const EmailStep = ({ initialEmail, onResolved }: Props) => {
  const { getFieldProps, canSubmit, onSubmit, formError, isLoading, setFormValues } = useForm<{ email: string }>({
    initialState: { email: '' },
    validationSchema,
    onSubmit: ({ email }) => {
      const trimmedEmail = email.trim();
      const result = resolveIdpFromEmail(trimmedEmail);

      if (result.type === 'password' && !isPasswordSignInEnabled()) {
        throw new Error(SIGN_IN_UNAVAILABLE_MESSAGE);
      }

      onResolved(trimmedEmail, result);
    },
  });

  // Seeded via setFormValues (not initialState) so `hasChanges` still trips and the Continue
  // button isn't stuck disabled when the user navigates back with an already-valid email.
  useEffectOnce(() => {
    if (initialEmail) {
      setFormValues({ email: initialEmail });
    }
  });

  const handleSubmit = async () => {
    await onSubmit();
  };

  return (
    <Form onSubmit={handleSubmit}>
      <InputText
        className="mb-6"
        labelText="Enter your email address"
        errors={formError}
        isPublic
        autoComplete="username"
        {...getFieldProps('email')}
      />
      <Button kind="primary" type="submit" isDisabled={!canSubmit} loading={isLoading} className="h-10 w-full">
        Continue with email
      </Button>
    </Form>
  );
};

export default EmailStep;
